import type { Settlement } from '../engine/map/settlement';
import { getGameConfig } from '../config/gameConfig';
import type { Rng } from './rng';

type WildlifeBucket = { age: number; amount: number };

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

function stochasticRound(value: number, rng: Rng): number {
    if (value <= 0) return 0;
    const base = Math.floor(value);
    const frac = value - base;
    return base + (rng() < frac ? 1 : 0);
}

function normalizeWildlife(buckets: WildlifeBucket[]): void {
    const merged = new Map<number, number>();
    for (const entry of buckets) {
        if (entry.amount <= 0) continue;
        const current = merged.get(entry.age) ?? 0;
        merged.set(entry.age, current + entry.amount);
    }
    buckets.length = 0;
    merged.forEach((amount, age) => {
        if (amount > 0) buckets.push({ age, amount });
    });
    buckets.sort((a, b) => a.age - b.age);
}

function totalTrees(trees: { age: number; amount: number }[]): number {
    return trees.reduce((sum, entry) => sum + entry.amount, 0);
}

export function totalWildlife(settlement: {
    regionResources: { wild_life: WildlifeBucket[] };
}): number {
    return settlement.regionResources.wild_life.reduce((sum, entry) => sum + entry.amount, 0);
}

export function getCarryingCapacity(settlement: Settlement): number {
    const { wildLife } = getGameConfig();
    const treesTotal = totalTrees(settlement.regionResources.trees);
    if (treesTotal <= 0) return 0;
    const treesCapacity = Math.floor(treesTotal / wildLife.treesPerAnimal);
    if (treesCapacity <= 0) return 0;
    if (wildLife.biomeCapWeight > 0) {
        const baseCap = settlement.regionResources.wildlifeCapBase ?? 0;
        if (baseCap > 0) {
            const weight = clamp(wildLife.biomeCapWeight, 0, 1);
            return Math.max(0, Math.round((1 - weight) * treesCapacity + weight * baseCap));
        }
    }
    return treesCapacity;
}

function addWildlife(settlement: Settlement, age: number, amount: number): void {
    if (amount <= 0) return;
    const existing = settlement.regionResources.wild_life.find((entry) => entry.age === age);
    if (existing) {
        existing.amount += amount;
    } else {
        settlement.regionResources.wild_life.push({ age, amount });
    }
}

function cullYoungest(buckets: WildlifeBucket[], count: number): number {
    if (count <= 0) return 0;
    buckets.sort((a, b) => a.age - b.age);
    let remaining = count;
    for (let i = 0; i < buckets.length && remaining > 0; i += 1) {
        const take = Math.min(remaining, buckets[i].amount);
        buckets[i].amount -= take;
        remaining -= take;
    }
    for (let i = buckets.length - 1; i >= 0; i -= 1) {
        if (buckets[i].amount <= 0) buckets.splice(i, 1);
    }
    return count - remaining;
}

export function runWildlifeTick(settlement: Settlement, rng: Rng = Math.random): boolean {
    const { wildLife } = getGameConfig();
    let changed = false;

    settlement.production.ecology.wildLifeTick += 1;
    const tick = settlement.production.ecology.wildLifeTick;
    const buckets = settlement.regionResources.wild_life;
    const capacity = getCarryingCapacity(settlement);

    if (tick % wildLife.matingInterval === 0) {
        const adults = buckets
            .filter((entry) => entry.age >= wildLife.matingAgeStart)
            .reduce((sum, entry) => sum + entry.amount, 0);
        if (adults > 0 && capacity > 0) {
            const total = totalWildlife(settlement);
            const effectiveCap = Math.max(1, Math.floor(capacity * wildLife.targetFill));
            const density = clamp(total / effectiveCap, 0, 2);
            const fertilityFactor = clamp(
                1 - Math.pow(density, wildLife.reproductionDensityPower),
                0,
                1,
            );
            const expectedBirths = adults * wildLife.fecundityPerAdult * fertilityFactor;
            const births = stochasticRound(expectedBirths, rng);
            if (births > 0) {
                addWildlife(settlement, wildLife.newbornAge, births);
                changed = true;
            }
        }
    }

    if (tick % wildLife.ageTicks === 0) {
        for (const entry of buckets) {
            entry.age += 1;
        }

        for (let i = buckets.length - 1; i >= 0; i -= 1) {
            const entry = buckets[i];
            if (entry.age >= wildLife.maxAge) {
                buckets.splice(i, 1);
                changed = true;
                continue;
            }
            let mortality = wildLife.baseMortalityPerAgeTick;
            if (entry.age <= wildLife.juvenileAgeEnd) {
                mortality += wildLife.juvenileExtraMortality;
            }
            if (entry.age >= wildLife.senescentAgeStart) {
                mortality +=
                    (entry.age - wildLife.senescentAgeStart + 1) * wildLife.senescentMortalitySlope;
            }
            mortality = clamp(mortality, 0, 0.95);
            const deaths = stochasticRound(entry.amount * mortality, rng);
            if (deaths > 0) {
                entry.amount = Math.max(0, entry.amount - deaths);
                changed = true;
            }
            if (entry.amount <= 0) {
                buckets.splice(i, 1);
                changed = true;
            }
        }

        const totalAfter = totalWildlife(settlement);
        if (totalAfter > capacity) {
            const excess = totalAfter - capacity;
            const cull = Math.ceil(excess * wildLife.overCapacityCullRate);
            if (cull > 0) {
                if (cullYoungest(buckets, cull) > 0) changed = true;
            }
        }

        normalizeWildlife(buckets);
    }

    return changed;
}
